import type {
	DocumentCategory,
	StorageUploadProgress,
	UploadGrant,
} from "@/adapters/api";
import { documentFileError } from "./file-types.ts";

export const UPLOAD_CONCURRENCY = 2;

export type UploadItemStatus =
	| "ready"
	| "rejected"
	| "queued"
	| "uploading"
	| "completing"
	| "done"
	| "failed";

export interface UploadBatchOptions {
	category: DocumentCategory;
	clientVisible: boolean;
}

export interface UploadItem {
	id: string;
	file: File;
	status: UploadItemStatus;
	progress: number;
	error?: string;
	options?: UploadBatchOptions;
	uploadedDocumentId?: string;
}

export interface UploadQueueDeps {
	start: (
		file: File,
		options: UploadBatchOptions,
	) => Promise<{ documentId: string; grant: UploadGrant }>;
	upload: (
		grant: UploadGrant,
		file: File,
		onProgress: (progress: StorageUploadProgress) => void,
		signal: AbortSignal,
	) => Promise<void>;
	complete: (documentId: string) => Promise<unknown>;
	errorMessage: (err: unknown) => string;
	restartRequired: (err: unknown) => boolean;
}

type Listener = () => void;

const ACTIVE_STATUSES: UploadItemStatus[] = ["uploading", "completing"];

export function isUploadActive(item: UploadItem): boolean {
	return ACTIVE_STATUSES.includes(item.status);
}

export function uploadPercent({
	loaded,
	total,
}: StorageUploadProgress): number {
	if (total <= 0) return 0;
	return Math.min(100, Math.max(0, Math.floor((loaded / total) * 100)));
}

function isAbortError(err: unknown): boolean {
	return err instanceof DOMException && err.name === "AbortError";
}

export class UploadQueue {
	private items: readonly UploadItem[] = [];
	private listeners = new Set<Listener>();
	private controllers = new Map<string, AbortController>();
	private sequence = 0;

	constructor(
		private deps: UploadQueueDeps,
		private concurrency = UPLOAD_CONCURRENCY,
	) {}

	getSnapshot = (): readonly UploadItem[] => this.items;

	subscribe = (listener: Listener): (() => void) => {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	};

	add(files: Iterable<File>): void {
		const added = Array.from(files, (file): UploadItem => {
			this.sequence += 1;
			const error = documentFileError(file);
			return {
				id: `upload-${this.sequence}`,
				file,
				status: error ? "rejected" : "ready",
				progress: 0,
				error,
			};
		});
		if (added.length === 0) return;
		this.set([...this.items, ...added]);
	}

	remove(id: string): void {
		this.controllers.get(id)?.abort();
		this.set(this.items.filter((item) => item.id !== id));
	}

	send(options: UploadBatchOptions): void {
		this.set(
			this.items.map((item) =>
				item.status === "ready" ? { ...item, status: "queued", options } : item,
			),
		);
		this.pump();
	}

	retry(id: string): void {
		this.set(
			this.items.map((item) =>
				item.id === id && item.status === "failed"
					? { ...item, status: "queued", progress: 0, error: undefined }
					: item,
			),
		);
		this.pump();
	}

	clearFinished(): void {
		this.set(
			this.items.filter(
				(item) => item.status !== "done" && item.status !== "rejected",
			),
		);
	}

	reset(): void {
		for (const controller of this.controllers.values()) controller.abort();
		this.controllers.clear();
		this.set([]);
	}

	private set(items: readonly UploadItem[]): void {
		this.items = items;
		for (const listener of this.listeners) listener();
	}

	private patch(id: string, patch: Partial<UploadItem>): boolean {
		let found = false;
		this.set(
			this.items.map((item) => {
				if (item.id !== id) return item;
				found = true;
				return { ...item, ...patch };
			}),
		);
		return found;
	}

	private pump(): void {
		let active = this.items.filter(isUploadActive).length;
		for (const item of this.items) {
			if (active >= this.concurrency) return;
			if (item.status !== "queued") continue;
			active += 1;
			this.patch(item.id, {
				status: item.uploadedDocumentId ? "completing" : "uploading",
			});
			void this.run(item);
		}
	}

	private async run(item: UploadItem): Promise<void> {
		const controller = new AbortController();
		this.controllers.set(item.id, controller);
		const { signal } = controller;
		let documentId = item.uploadedDocumentId;
		try {
			if (!documentId) {
				const options = item.options;
				if (!options) throw new Error("missing upload options");
				const started = await this.deps.start(item.file, options);
				if (signal.aborted) return;
				let lastPercent = 0;
				await this.deps.upload(
					started.grant,
					item.file,
					(progress) => {
						const percent = uploadPercent(progress);
						if (percent === lastPercent) return;
						lastPercent = percent;
						this.patch(item.id, { progress: percent });
					},
					signal,
				);
				documentId = started.documentId;
				if (
					!this.patch(item.id, {
						status: "completing",
						progress: 100,
						uploadedDocumentId: documentId,
					})
				) {
					return;
				}
			}
			await this.deps.complete(documentId);
			this.patch(item.id, { status: "done", progress: 100 });
		} catch (err) {
			if (isAbortError(err) || signal.aborted) return;
			const restart = this.deps.restartRequired(err);
			this.patch(item.id, {
				status: "failed",
				error: this.deps.errorMessage(err),
				uploadedDocumentId: restart ? undefined : documentId,
				progress: restart || !documentId ? 0 : 100,
			});
		} finally {
			if (this.controllers.get(item.id) === controller) {
				this.controllers.delete(item.id);
			}
			this.pump();
		}
	}
}
